import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { EnvelopeSimple, Lock, ArrowRight, User } from '@phosphor-icons/react';

export default function AuthScreen() {
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [registrationPending, setRegistrationPending] = useState(false);
  const [verificationMsg, setVerificationMsg] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [resetToken, setResetToken] = useState('');
  // EFFECTIVE-PLAN-SESSION-ONBOARDING-001, part G: `purpose` is a purely
  // presentational hint carried on the admin-issued setup link's own URL
  // — the actual authorization always comes from the token's real
  // `purpose` field on the server, never from this query param. Lets
  // this screen show "set up your new account" copy instead of the
  // normal password-reset copy, without a new backend lookup.
  const [isAdminInvite, setIsAdminInvite] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [mode, setMode] = useState('login');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('reset_token');
    const purpose = params.get('purpose');
    const billing = params.get('billing') === 'success';
    const requestedMode = params.get('mode');
    const verificationToken = params.get('verify_email');
    setBillingSuccess(billing);
    if (billing || requestedMode === 'register') setMode('register');
    if (rt) {
      setResetToken(rt);
      setShowForgot(true);
      setIsAdminInvite(purpose === 'admin_invite');
    }
    if (verificationToken) {
      setLoading(true);
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-email`, { token: verificationToken })
        .then(() => {
          setVerificationMsg(t('auth.emailVerified'));
          setMode('login');
        })
        .catch((err) => {
          setVerificationError(err.response?.data?.detail || t('auth.verifyEmailError'));
        })
        .finally(() => {
          setLoading(false);
          window.history.replaceState({}, '', window.location.pathname);
        });
    }
  }, [t]);

  const isRegisterMode = mode === 'register';
  const lockRegistrationFlow = billingSuccess;

  const handleForgot = async () => {
    setForgotMsg('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotMsg(data.message || 'Link di reset inviato.');
    } catch {
      setForgotMsg('Errore. Riprova.');
    }
  };

  const handleReset = async () => {
    setResetMsg('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
      setResetMsg(data.message || 'Password aggiornata!');
      setResetToken('');
      setShowForgot(false);
    } catch (e) {
      setResetMsg(e.response?.data?.detail || 'Errore. Riprova.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegisterMode) {
        if (password !== confirmPassword) {
          setError(t('auth.passwordMismatch'));
          return;
        }
        const data = await register(name.trim(), email, password);
        if (data.requires_email_verification) {
          setRegistrationPending(true);
          setVerificationMsg(t('auth.checkEmail'));
        }
      } else {
        await login(email, password);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t(isRegisterMode ? 'auth.registerError' : 'auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setVerificationError('');
    try {
      const language = localStorage.getItem('i18nextLng') || document.documentElement.lang || 'it';
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
        email,
        language: String(language).slice(0, 2),
      });
      setVerificationMsg(t('auth.verificationResent'));
    } catch (err) {
      setVerificationError(err.response?.data?.detail || t('auth.verifyEmailError'));
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="auth-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="auth-box"
      >
        <div className="mb-8 text-center">
          <div data-testid="auth-logo" style={{ height: 50, margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/assets/LOGO-sketchario.jpg" alt="Sketchario" style={{ height: 50, objectFit: 'contain', borderRadius: 8 }} />
          </div>
          <h1 className="text-2xl font-semibold mb-2">
            {isRegisterMode ? t('auth.registerTitle') : t('auth.loginTitle')}
          </h1>
          <p className="text-[var(--text-secondary)] text-sm">
            {billingSuccess
              ? 'Pagamento completato. Crea ora il tuo account per accedere a Sketchario.'
              : isRegisterMode
                ? t('auth.registerSubtitle')
                : t('auth.loginSubtitle')}
          </p>
        </div>

        {verificationMsg && !registrationPending && (
          <div className="mb-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-300">
            {verificationMsg}
          </div>
        )}
        {verificationError && !registrationPending && (
          <div className="mb-5 rounded-xl border border-pink-500/30 bg-pink-500/10 p-4 text-sm text-[var(--accent-pink)]">
            {verificationError}
          </div>
        )}

        {registrationPending ? (
          <div className="text-center">
            <EnvelopeSimple size={42} className="mx-auto mb-4 text-[var(--gradient-start)]" />
            <h2 className="text-xl font-semibold mb-3">{t('auth.checkEmailTitle')}</h2>
            <p className="text-sm text-[var(--text-secondary)] leading-6 mb-2">{verificationMsg}</p>
            <p className="text-sm font-medium mb-6">{email}</p>
            {verificationError && <p className="text-sm text-[var(--accent-pink)] mb-4">{verificationError}</p>}
            <button
              type="button"
              className="btn-ghost w-full"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification ? t('auth.resendingVerification') : t('auth.resendVerification')}
            </button>
            <button
              type="button"
              className="mt-4 text-sm text-[var(--text-muted)] hover:text-white underline"
              onClick={() => {
                setRegistrationPending(false);
                setVerificationMsg('');
                setMode('login');
              }}
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegisterMode && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('auth.name')}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  className="input-dark"
                  placeholder="Il tuo nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('auth.email')}</label>
            <div className="relative">
              <EnvelopeSimple className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
              <input
                data-testid="auth-email-input"
                type="email"
                className="input-dark"
                placeholder="email@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
              <input
                data-testid="auth-password-input"
                type="password"
                className="input-dark"
                placeholder={t('auth.passwordRequirementsShort')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                maxLength={128}
              />
            </div>
            {isRegisterMode && (
              <p className="mt-2 text-xs leading-5 text-[var(--text-muted)]">
                {t('auth.passwordRequirements')}
              </p>
            )}
          </div>

          {isRegisterMode && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('auth.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
                <input
                  data-testid="auth-confirm-password-input"
                  type="password"
                  className="input-dark"
                  placeholder={t('auth.confirmPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={12}
                  maxLength={128}
                />
              </div>
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-sm text-[var(--accent-pink)]">{error}</p>}

          <button data-testid="auth-submit-btn" className="btn-gradient w-full mt-6" type="submit" disabled={loading}>
            {loading
              ? (isRegisterMode ? t('auth.registering') : t('auth.loggingIn'))
              : (isRegisterMode ? t('auth.register') : t('auth.login'))}
            {!loading && <ArrowRight weight="bold" size={18} />}
          </button>

          {!isRegisterMode && (
            <p className="text-center mt-3">
              <button
                type="button"
                data-testid="forgot-password-link"
                className="text-sm text-[var(--text-muted)] hover:text-[var(--gradient-start)] underline"
                onClick={() => setShowForgot(true)}
              >
                {t('auth.forgotPassword')}
              </button>
            </p>
          )}
        </form>
        )}

        {lockRegistrationFlow && !registrationPending && (
          <p className="text-center mt-6 text-xs text-[var(--text-muted)]">
            Dopo la registrazione entrerai direttamente nella tua area Sketchario.
          </p>
        )}
      </motion.div>

      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowForgot(false)}>
          <div className="card w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            {resetToken ? (
              <>
                <h3 className="text-lg font-semibold mb-4" data-testid="reset-password-title">
                  {isAdminInvite ? t('auth.setupNewAccountTitle') : t('auth.newPassword')}
                </h3>
                <input
                  type="password"
                  className="input-dark mb-3"
                  placeholder={t('auth.newPassword')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={12}
                  maxLength={128}
                  style={{ paddingLeft: '1rem' }}
                />
                <p className="text-xs leading-5 text-[var(--text-muted)] mb-3">{t('auth.passwordRequirements')}</p>
                {resetMsg && <p className="text-sm mb-3" style={{ color: resetMsg.includes('aggiornata') ? 'var(--accent-green)' : 'var(--accent-pink)' }}>{resetMsg}</p>}
                <button className="btn-gradient w-full" onClick={handleReset} data-testid="reset-password-btn">{t('common.confirm')}</button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">{t('auth.forgotPassword')}</h3>
                <p className="text-sm text-[var(--text-muted)] mb-4">Inserisci la tua email per ricevere un link di reset.</p>
                <input
                  type="email"
                  className="input-dark mb-3"
                  placeholder="email@esempio.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  style={{ paddingLeft: '1rem' }}
                />
                {forgotMsg && <p className="text-sm text-[var(--accent-green)] mb-3">{forgotMsg}</p>}
                <button className="btn-gradient w-full" onClick={handleForgot} data-testid="send-reset-btn">Invia Link Reset</button>
              </>
            )}
            <button className="btn-ghost w-full mt-3" onClick={() => { setShowForgot(false); setResetToken(''); }}>{t('common.close')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
